import React, { useEffect } from "react";
import { ConfigProvider, App as AntdApp, Spin } from "antd";
import "remixicon/fonts/remixicon.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";

// 页面
import Documentation from "./pages/Documentation";
import DocumentCenter from "./pages/DocumentCenter";
import AIChat from "./pages/AIChat";
import Knowledge from "./pages/Knowledge";
import UserSettings from "./pages/Settings";
import Invitation from "./pages/Invitation";
import ChatAPI from "./pages/ChatAPI";
import ChatHistory from "./pages/ChatHistory";
import CodeGenerator from "./pages/CodeGenerator";
import EventAnnotations from "./pages/EventAnnotations";
import RedeemCodeGenerator from "./pages/admin/RedeemCodeGenerator";
import OrderManagement from "./pages/admin/OrderManagement";
import DataMigration from "./pages/admin/DataMigration";
import Login from "./pages/Login";
import MyOrders from "./pages/MyOrders";
import UserManagement from "./pages/admin/UserManagement";
import QQCallback from "./pages/QQCallback";
import GitHubCallback from "./pages/GitHubCallback";
import ProductManagement from "./pages/admin/ProductManagement";
import TokenUsage from "./pages/TokenUsage";
import About from "./pages/About";
import Notice from "./pages/Notice";
import MCPServer from "./pages/MCPServer";
import PaymentCheckout from "./pages/PaymentCheckout";
import Podcast from "./pages/Podcast";
import Agents from "./pages/Agents";
import ModelCenter from "./pages/ModelCenter";
import ModelManagement from "./pages/admin/ModelManagement";
import ModelGateway from "./pages/admin/ModelGateway";
import Analytics from "./pages/Analytics";
import AgentAudit from "./pages/AgentAudit";
import SiteMemory from "./pages/SiteMemory";
import AgentConfig from "./pages/AgentConfig";
import SystemSettings from "./pages/admin/SystemSettings";
import DataControl from "./pages/admin/DataControl";

// 新外壳 / 项目工作区
import ConsoleLayout from "./layouts/ConsoleLayout";
import ProjectGuard from "./layouts/ProjectGuard";
import RoleGuard from "./layouts/RoleGuard";
import ProjectList from "./pages/projects/ProjectList";
import SiteOnboarding from "./pages/sites/SiteOnboarding";
import SiteOverview from "./pages/sites/SiteOverview";
import SiteCopilot from "./pages/sites/SiteCopilot";
import SiteCopilotPlaybook from "./pages/sites/SiteCopilotPlaybook";
import SiteKnowledge from "./pages/sites/SiteKnowledge";
import SiteAbilities from "./pages/sites/SiteAbilities";
import SiteTestLab from "./pages/sites/SiteTestLab";
import SiteAppearance from "./pages/sites/SiteAppearance";
import SiteAnalytics from "./pages/sites/SiteAnalytics";
import SiteSettings from "./pages/sites/SiteSettings";
import SitePublish from "./pages/sites/SitePublish";
import SiteWorkspaceLayout from "./layouts/SiteWorkspaceLayout";
import ProjectOverview from "./pages/projects/ProjectOverview";
import ProjectSettings from "./pages/projects/ProjectSettings";
import ProjectSummarySettings from "./pages/projects/ProjectSummarySettings";
import ProjectUsage from "./pages/projects/ProjectUsage";

import "./styles/Breadcrumb.css";
import "./styles/global.css";
import "./App.css";
import { GlobalProvider, useGlobal } from "./contexts/GlobalContext";
import { checkToken } from "./utils/auth";
import { resolveDefaultProjectKey } from "./hooks/useCurrentProject";
import {
  legacyProjectKeyFromSearch,
  legacyProjectTarget,
  sitesPath,
} from "./config/nav";
import { useTranslation } from "./i18n";

const APP_BASENAME = "/console";
const AdminAuditLogs = React.lazy(() => import("./pages/admin/AuditLogs"));
const AdminExecutionReplay = React.lazy(() => import("./pages/admin/ExecutionReplay"));
const AdminDeletionDeliveries = React.lazy(() => import("./pages/admin/DeletionDeliveries"));
const AdminProjectInventory = React.lazy(() => import("./pages/admin/ProjectInventory"));
const AdminDomainInventory = React.lazy(() => import("./pages/admin/DomainInventory"));
const AdminAPISecretInventory = React.lazy(() => import("./pages/admin/APISecretInventory"));
const AdminGeneratedContentInventory = React.lazy(() => import("./pages/admin/GeneratedContentInventory"));
const AdminSafetyEvents = React.lazy(() => import("./pages/admin/SafetyEvents"));

// 登录态守卫：token 失效且不在登录/分享页 → 去登录页。
const AuthWatcher: React.FC = () => {
  const { token, setToken } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isLoginPage = location.pathname.startsWith("/login");
    const isSharePage = location.pathname.startsWith("/agent/share/");
    if (!checkToken(token) && !isLoginPage && !isSharePage) {
      setToken(null);
      navigate("/login");
    }
  }, [token, location.pathname, navigate, setToken]);

  return null;
};

// 旧的全局功能页 → 项目作用域重定向。无项目则去项目列表。
const LegacyProjectRedirect: React.FC<{ sub: string }> = ({ sub }) => {
  const { projects, loading } = useGlobal();
  const location = useLocation();
  if (loading && projects.length === 0) {
    return <div style={{ padding: 80, textAlign: "center" }}><Spin /></div>;
  }
  const key = legacyProjectKeyFromSearch(location.search) || resolveDefaultProjectKey(projects);
  return (
    <Navigate
      to={key ? legacyProjectTarget(key, sub, location.search, location.hash) : `/${location.search}${location.hash}`}
      replace
    />
  );
};

// 旧 /agents/:id/{config,studio} → 项目作用域（保留 :id）。
const LegacyAgentRedirect: React.FC<{ kind: "config" | "studio" }> = ({ kind }) => {
  const { id } = useParams<{ id: string }>();
  const { projects, loading } = useGlobal();
  const location = useLocation();
  if (loading && projects.length === 0) {
    return <div style={{ padding: 80, textAlign: "center" }}><Spin /></div>;
  }
  const key = legacyProjectKeyFromSearch(location.search) || resolveDefaultProjectKey(projects);
  return (
    <Navigate
      to={key ? legacyProjectTarget(key, `agents/${id}/${kind}`, location.search, location.hash) : `/${location.search}${location.hash}`}
      replace
    />
  );
};

const guard = (el: React.ReactNode) => <ProjectGuard>{el}</ProjectGuard>;

const lazyAdmin = (el: React.ReactNode) => (
  <React.Suspense fallback={<div style={{ padding: 80, textAlign: "center" }}><Spin /></div>}>
    {el}
  </React.Suspense>
);

const SitesRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={sitesPath(location.search, location.hash)} replace />;
};

const MainRoutes: React.FC = () => (
  <Routes>
    {/* 项目工作区 */}
    <Route path="/p/:projectKey">
      <Route index element={<Navigate to="overview" replace />} />
      <Route path="overview" element={guard(<ProjectOverview />)} />
      <Route path="knowledge" element={guard(<Knowledge />)} />
      <Route path="documents" element={guard(<DocumentCenter />)} />
      <Route path="agents" element={guard(<Agents />)} />
      <Route path="agents/:id/config" element={guard(<AgentConfig />)} />
      <Route path="agents/:id/studio" element={<Navigate to="../config" replace />} />
      <Route path="website" element={guard(<CodeGenerator />)} />
      <Route path="summary-settings" element={guard(<ProjectSummarySettings />)} />
      <Route path="growth" element={guard(<Podcast />)} />
      <Route path="analytics" element={guard(<Analytics />)} />
      <Route path="chat-history" element={guard(<ChatHistory />)} />
      <Route path="usage" element={guard(<ProjectUsage />)} />
      <Route path="agent-audit" element={guard(<AgentAudit />)} />
      <Route path="memories" element={guard(<SiteMemory />)} />
      <Route path="annotations" element={guard(<EventAnnotations />)} />
      <Route path="settings" element={guard(<ProjectSettings />)} />
    </Route>

    {/* 全局 · 工作台 / 平台 / 账务 / 设置 */}
    <Route path="/projects" element={<ProjectList />} />
    {/* 旧“网站”入口只保留兼容跳转，不再作为控制台信息架构。 */}
    <Route path="/sites" element={<SitesRedirect />} />
    <Route path="/sites/:siteId/setup" element={<SiteOnboarding />} />
    <Route path="/sites/:siteId" element={<SiteWorkspaceLayout />}>
      <Route index element={<SiteOverview />} />
      <Route path="copilot" element={<SiteCopilot />} />
      <Route path="playbook" element={<SiteCopilotPlaybook />} />
      <Route path="knowledge" element={<SiteKnowledge />} />
      <Route path="abilities" element={<SiteAbilities />} />
      <Route path="test" element={<SiteTestLab />} />
      <Route path="appearance" element={<SiteAppearance />} />
      <Route path="analytics" element={<SiteAnalytics />} />
      <Route path="settings" element={<SiteSettings />} />
      <Route path="publish" element={<SitePublish />} />
    </Route>
    <Route path="/" element={<SitesRedirect />} />
    <Route path="/models" element={<ModelCenter />} />
    <Route path="/mcp-server" element={<MCPServer />} />
    <Route path="/ai-chat" element={<AIChat />} />
    <Route path="/docs" element={<Documentation />} />
    <Route path="/api" element={<ChatAPI />} />
    <Route path="/my-orders" element={<MyOrders />} />
    <Route path="/token-usage" element={<TokenUsage />} />
    <Route path="/invitation" element={<Invitation />} />
    <Route path="/settings" element={<UserSettings />} />
    <Route path="/about" element={<About />} />
    <Route path="/notice" element={<Notice />} />
    <Route path="/payment-checkout" element={<PaymentCheckout />} />

    {/* 管理台：统一 Route 级角色守卫（后端仍强校验，这里消除“手输 URL 进空壳”） */}
    <Route element={<RoleGuard require="admin"><Outlet /></RoleGuard>}>
      <Route path="/user-management" element={<UserManagement />} />
      <Route path="/product-management" element={<ProductManagement />} />
      <Route path="/order-management" element={<OrderManagement />} />
      <Route path="/redeem-code-generator" element={<RedeemCodeGenerator />} />
      <Route path="/data-control" element={<DataControl />} />
      <Route path="/audit-logs" element={lazyAdmin(<AdminAuditLogs />)} />
      <Route path="/execution-replay" element={lazyAdmin(<AdminExecutionReplay />)} />
      <Route path="/deletion-deliveries" element={lazyAdmin(<AdminDeletionDeliveries />)} />
      <Route path="/project-inventory" element={lazyAdmin(<AdminProjectInventory />)} />
      <Route path="/domain-inventory" element={lazyAdmin(<AdminDomainInventory />)} />
      <Route path="/api-secret-inventory" element={lazyAdmin(<AdminAPISecretInventory />)} />
      <Route path="/generated-content-inventory" element={lazyAdmin(<AdminGeneratedContentInventory />)} />
      <Route path="/safety-events" element={lazyAdmin(<AdminSafetyEvents />)} />
      <Route path="/model-gateway" element={<ModelGateway />} />
      <Route path="/system-settings" element={<SystemSettings />} />
      <Route path="/model-management" element={<ModelManagement />} />
    </Route>
    {/* 数据迁移含跨用户导入，后端为 superadmin 专属，前端同级守卫 */}
    <Route element={<RoleGuard require="superadmin"><Outlet /></RoleGuard>}>
      <Route path="/data-migration" element={<DataMigration />} />
    </Route>

    {/* 旧路由 → 项目作用域重定向 */}
    <Route path="/knowledge" element={<LegacyProjectRedirect sub="knowledge" />} />
    <Route path="/agents" element={<LegacyProjectRedirect sub="agents" />} />
    <Route path="/agents/:id/config" element={<LegacyAgentRedirect kind="config" />} />
    <Route path="/agents/:id/studio" element={<LegacyAgentRedirect kind="studio" />} />
    <Route path="/analytics" element={<LegacyProjectRedirect sub="analytics" />} />
    <Route path="/podcast" element={<LegacyProjectRedirect sub="growth" />} />
    <Route path="/chat-history" element={<LegacyProjectRedirect sub="chat-history" />} />
    <Route path="/annotations" element={<LegacyProjectRedirect sub="annotations" />} />
    <Route path="/agent-audit" element={<LegacyProjectRedirect sub="agent-audit" />} />
    <Route path="/code-generator" element={<LegacyProjectRedirect sub="website" />} />
    <Route path="/workflow-management" element={<LegacyProjectRedirect sub="agents" />} />
    <Route path="/documents" element={<LegacyProjectRedirect sub="documents" />} />
    <Route path="/summary-settings" element={<LegacyProjectRedirect sub="summary-settings" />} />

    <Route path="*" element={<Navigate to="/projects" replace />} />
  </Routes>
);

const AppContent: React.FC = () => (
  <>
    <AuthWatcher />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/qq" element={<QQCallback />} />
      <Route path="/login/github" element={<GitHubCallback />} />
      <Route path="/agent/share/:token" element={<Navigate to="/agents" replace />} />
      <Route
        path="/*"
        element={
          <ConsoleLayout>
            <MainRoutes />
          </ConsoleLayout>
        }
      />
    </Routes>
  </>
);

const App: React.FC = () => {
  const { antdLocale } = useTranslation();

  if (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/agent/share/")
  ) {
    const nextPath = `${APP_BASENAME}${window.location.pathname}`;
    window.location.replace(
      `${window.location.origin}${nextPath}${window.location.search}${window.location.hash}`
    );
    return null;
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: "#3979F6",
          borderRadius: 6,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          marginXS: 8,
          marginSM: 12,
          marginMD: 16,
          marginLG: 24,
          marginXL: 32,
          padding: 16,
          paddingSM: 12,
          paddingLG: 24,
          fontSize: 14,
          controlHeight: 32,
        },
        components: {
          Button: { borderRadius: 8, paddingInline: 16 },
          Input: { borderRadius: 4 },
          Card: { borderRadius: 12 },
        },
      }}
    >
      <AntdApp>
        <BrowserRouter
          basename={APP_BASENAME}
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <GlobalProvider>
            <AppContent />
          </GlobalProvider>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
